import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, INR, calcEMI } from "@/lib/api";
import {
  ArrowRight, ShieldCheck, Timer, BadgePercent, Users, Star,
  Wallet, Briefcase, Home as HomeIcon, Car, Gem, GraduationCap, Building2, Landmark, Cog,
} from "lucide-react";

const ICONS = {
  wallet: Wallet, briefcase: Briefcase, home: HomeIcon, car: Car,
  gem: Gem, "graduation-cap": GraduationCap, "building-2": Building2, banknote: Wallet,
  landmark: Landmark, cog: Cog,
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(10.5);
  const [months, setMonths] = useState(36);

  useEffect(() => {
    api.get("/loan-products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);
  const emi = calcEMI(amount, rate, months);
  const total = emi * months;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="sk-hero-grad sk-grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7 sk-fade-up">
            <span className="sk-badge sk-badge-gold" data-testid="hero-badge">
              <ShieldCheck size={12}/> Government Banks · NBFC · Since 2015
            </span>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-white">
              Trusted <span className="gold italic">Loan &amp; Insurance</span><br/>Solutions for Bharat.
            </h1>
            <p className="mt-6 text-white/70 text-lg max-w-xl leading-relaxed">
              Home · Business · Personal · Mortgage · Machinery · LAP · Insurance — sourced from top
              government banks &amp; NBFCs with transparent pricing and approvals in <span className="text-white">24 hours</span>.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/apply" data-testid="hero-apply" className="sk-btn-gold inline-flex items-center gap-2">
                Apply for a Loan <ArrowRight size={18}/>
              </Link>
              <Link to="/emi-calculator" data-testid="hero-emi" className="sk-btn-ghost">Calculate EMI</Link>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "5,000+", v: "Happy customers" },
                { k: "₹100 Cr+", v: "Loans assisted" },
                { k: "10+ Yrs", v: "Experience" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-display text-2xl text-white">{s.k}</div>
                  <div className="text-xs text-white/50 uppercase tracking-widest mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero right — female advisor + EMI stacked */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
            className="lg:col-span-5 space-y-6"
          >
            {/* Advisor portrait card */}
            <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/30" data-testid="advisor-card">
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&h=1100&fit=crop&crop=faces"
                alt="Your loan advisor at SK Group"
                className="w-full h-80 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-transparent"/>
              <div className="absolute top-4 left-4 sk-badge sk-badge-gold">
                <ShieldCheck size={12}/> Certified advisor
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-display text-2xl text-white">Priya · Your dedicated RM</div>
                <div className="text-white/70 text-sm mt-1">"I'll personally guide your loan from application to disbursal."</div>
              </div>
            </div>

            {/* Quick EMI mini card */}
            <div className="sk-card-dark p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Instant EMI</div>
                  <div className="font-display text-xl text-white mt-1">Plan your repayment</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-white/50 uppercase tracking-widest">Monthly</div>
                  <div className="font-display text-2xl text-[#D4AF37]" data-testid="hero-emi-value">{INR(emi)}</div>
                </div>
              </div>
              <div className="sk-divider my-4"/>
              <label className="block text-[11px] uppercase tracking-widest text-white/60 mb-2">Loan amount · <span className="text-white/85 normal-case tracking-normal">{INR(amount)}</span></label>
              <input type="range" min="50000" max="10000000" step="10000" value={amount}
                onChange={(e) => setAmount(+e.target.value)}
                data-testid="hero-amount"
                className="w-full accent-[#D4AF37]"/>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-white/60 mb-1">Rate %</label>
                  <input type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)}
                    data-testid="hero-rate"
                    className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-[#D4AF37] outline-none text-sm"/>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-white/60 mb-1">Tenure (mo)</label>
                  <input type="number" value={months} onChange={(e) => setMonths(+e.target.value)}
                    data-testid="hero-tenure"
                    className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-[#D4AF37] outline-none text-sm"/>
                </div>
              </div>
              <Link to="/apply" className="mt-5 block text-center sk-btn-gold text-sm py-2.5">Get pre-approved</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST bar */}
      <section className="bg-black py-6 border-y border-white/5 overflow-hidden">
        <div className="sk-marquee">
          {[...Array(2)].flatMap((_, i) => [
            "RBI-registered NBFC partner", "No hidden charges", "Approval in 24 hrs",
            "Interest from 7.5% p.a.", "Digital onboarding", "PAN-India service",
            "Dedicated relationship manager", "Insured document vault",
          ].map((t, j) => (
            <div key={`${i}-${j}`} className="px-10 flex items-center gap-4 text-white/40 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"/> {t}
            </div>
          )))}
        </div>
      </section>

      {/* LOAN PRODUCTS */}
      <section id="products" className="bg-[#0B0B0B] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-14">
            <div>
              <div className="sk-badge sk-badge-gold">Our Offerings</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-white max-w-2xl leading-tight">
                Seven loan products.<br/><span className="gold italic">One trusted partner.</span>
              </h2>
            </div>
            <Link to="/loans" className="sk-btn-ghost text-sm">Explore all →</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => {
              const Icon = ICONS[p.icon] || Wallet;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.5 }}
                >
                  <Link to={`/loans/${p.slug}`} data-testid={`product-${p.slug}`} className="sk-card-dark p-7 block h-full">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mb-5">
                      <Icon size={22} className="text-[#D4AF37]"/>
                    </div>
                    <h3 className="font-display text-2xl text-white">{p.name}</h3>
                    <p className="text-white/60 mt-2 text-sm min-h-[3rem]">{p.tagline}</p>
                    <div className="sk-divider my-5"/>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-white/40 uppercase tracking-wider">From</div>
                        <div className="text-white mt-1 font-medium">{p.interest_rate_from}% p.a.</div>
                      </div>
                      <div>
                        <div className="text-white/40 uppercase tracking-wider">Up to</div>
                        <div className="text-white mt-1 font-medium">{INR(p.max_amount)}</div>
                      </div>
                    </div>
                    <div className="mt-5 text-[#D4AF37] text-sm font-medium inline-flex items-center gap-1">
                      Know more <ArrowRight size={14}/>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#111111] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="sk-badge sk-badge-gold">Why SK Group</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-white leading-tight">
              Built on trust.<br/><span className="gold italic">Powered by speed.</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-md leading-relaxed">
              We combine deep industry expertise with modern technology to bring you a lending
              experience that is fast, fair and transparent.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: BadgePercent, t: "Lowest Interest", d: "Rates starting at just 7.5% p.a. sourced from top government banks & NBFCs." },
              { icon: Timer, t: "Fast Approval", d: "Digital-first processing gets you a decision in 24 hours or less." },
              { icon: ShieldCheck, t: "Trusted Guidance", d: "10+ years advising 5,000+ customers on the right loan & insurance products." },
              { icon: ShieldCheck, t: "Secure Process", d: "Bank-grade 256-bit encryption. Your KYC is stored in an insured vault." },
              { icon: Users, t: "Experienced Team", d: "Dedicated relationship manager from application to disbursal." },
              { icon: BadgePercent, t: "Transparent Service", d: "Every charge disclosed upfront. Zero hidden fees, guaranteed." },
            ].map((f, i) => (
              <div key={i} className="sk-card-dark p-6">
                <f.icon className="text-[#D4AF37]" size={22}/>
                <div className="mt-4 text-white font-medium text-lg">{f.t}</div>
                <div className="text-white/55 text-sm mt-1">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#0B0B0B] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center">
            <div className="sk-badge sk-badge-gold mx-auto">Customer stories</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-white">Trusted by <span className="gold italic">5,000+</span> Indians</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              { n: "Priya Sharma", c: "Mumbai", q: "Got my home loan approved in 22 hours. The team was transparent about every rupee." },
              { n: "Rajesh Verma", c: "Pune", q: "Their business loan helped us expand to two new outlets. Zero paperwork stress." },
              { n: "Anita Iyer", c: "Bengaluru", q: "The gold loan disbursal was instant. Best per-gram rate I found in the city." },
            ].map((t, i) => (
              <div key={i} className="sk-card-dark p-8">
                <div className="flex gap-1 text-[#D4AF37]">{Array.from({length:5}).map((_,k)=><Star key={k} size={14} fill="currentColor"/>)}</div>
                <p className="text-white/85 mt-5 leading-relaxed">"{t.q}"</p>
                <div className="mt-6 text-sm">
                  <div className="text-white font-medium">{t.n}</div>
                  <div className="text-white/40 text-xs">{t.c}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#111] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-white leading-tight">Ready to <span className="gold italic">get started?</span></h2>
          <p className="text-white/60 mt-4">Apply in under 3 minutes. Get a decision the same day.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/apply" className="sk-btn-gold">Apply Now</Link>
            <Link to="/contact" className="sk-btn-ghost">Talk to an expert</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
